'use client'

import * as React from 'react'
import { TrendingUp, ShoppingCart, BadgeDollarSign, LayoutGrid } from 'lucide-react'

// Sample attribution dashboard. Illustrative only: every number here is
// sample data, never a claimed customer result. The product names are the
// approved Idukki marketing sample cards (see CLAUDE.md). A period toggle
// rescales the figures so the surface feels live without faking a result.

type Row = {
  piece: string
  source: 'Instagram' | 'TikTok' | 'YouTube' | 'Reviews' | 'UGC gallery'
  revenue: number
  carts: number
  roas: number
}

type Period = '7d' | '30d' | '90d'

// Base (30-day) sample set, then scaled for the other windows.
const BASE: Row[] = [
  { piece: 'Airlift High-Waist Shorts', source: 'Instagram',   revenue: 41200, carts: 612, roas: 4.8 },
  { piece: 'White Sweater Green Stripes', source: 'TikTok',     revenue: 28600, carts: 430, roas: 3.9 },
  { piece: 'Women Oval Sunglasses',      source: 'Reviews',     revenue: 19100, carts: 318, roas: 3.4 },
  { piece: 'WROGN Silver-Toned Watch',   source: 'YouTube',     revenue: 12700, carts: 184, roas: 2.7 },
  { piece: 'Jute Tote Bag',              source: 'UGC gallery', revenue: 8300,  carts: 142, roas: 2.1 },
]

const SCALE: Record<Period, number> = { '7d': 0.26, '30d': 1, '90d': 2.74 }

function money(n: number): string {
  return n >= 1000 ? `$${(n / 1000).toFixed(1)}K` : `$${n}`
}

const SOURCE_TONE: Record<Row['source'], string> = {
  Instagram: 'bg-pink-500/12 text-pink-600 dark:text-pink-400',
  TikTok: 'bg-foreground/8 text-foreground',
  YouTube: 'bg-red-500/12 text-red-600 dark:text-red-400',
  Reviews: 'bg-amber-500/12 text-amber-600 dark:text-amber-500',
  'UGC gallery': 'bg-primary/12 text-primary',
}

export function AttributionDashboard() {
  const [period, setPeriod] = React.useState<Period>('30d')
  const scale = SCALE[period]

  const rows = BASE.map((r) => ({
    ...r,
    revenue: Math.round((r.revenue * scale) / 100) * 100,
    carts: Math.round(r.carts * scale),
  }))
  const totalRevenue = rows.reduce((s, r) => s + r.revenue, 0)
  const totalCarts = rows.reduce((s, r) => s + r.carts, 0)
  const blendedRoas = (rows.reduce((s, r) => s + r.roas, 0) / rows.length).toFixed(1)

  const kpis = [
    { icon: BadgeDollarSign, label: 'Attributed revenue', value: money(totalRevenue) },
    { icon: ShoppingCart, label: 'Add-to-carts', value: totalCarts.toLocaleString() },
    { icon: TrendingUp, label: 'Blended ROAS', value: `${blendedRoas}x` },
    { icon: LayoutGrid, label: 'Widgets live', value: '6' },
  ]

  return (
    <div className="overflow-hidden rounded-3xl border bg-card shadow-2xl shadow-primary/5">
      {/* Header: title, sample chip, period toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4 sm:px-6">
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2 w-2" aria-hidden>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500/70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          <h3 className="font-display text-base font-semibold tracking-tight">Content revenue</h3>
          <span className="rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
            Sample
          </span>
        </div>
        <div role="tablist" aria-label="Time period" className="inline-flex rounded-lg bg-secondary p-0.5">
          {(['7d', '30d', '90d'] as const).map((p) => (
            <button
              key={p}
              role="tab"
              aria-selected={period === p}
              onClick={() => setPeriod(p)}
              className={`rounded-md px-3 py-1 text-xs font-semibold transition ${
                period === p ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* KPI strip */}
      <dl className="grid grid-cols-2 divide-x divide-y border-b sm:grid-cols-4 sm:divide-y-0">
        {kpis.map((k) => {
          const Icon = k.icon
          return (
            <div key={k.label} className="px-5 py-4">
              <dt className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <Icon className="h-3.5 w-3.5" /> {k.label}
              </dt>
              <dd className="mt-1.5 font-display text-2xl font-bold tabular-nums tracking-tight">{k.value}</dd>
            </div>
          )
        })}
      </dl>

      {/* Leaderboard */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
              <th scope="col" className="px-5 py-2.5 font-medium sm:px-6">Content piece</th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">Revenue</th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">Carts</th>
              <th scope="col" className="px-5 py-2.5 text-right font-medium sm:px-6">ROAS</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.piece} className="border-t">
                <td className="px-5 py-3 sm:px-6">
                  <div className="font-medium leading-tight text-foreground">{r.piece}</div>
                  <span className={`mt-1 inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${SOURCE_TONE[r.source]}`}>
                    {r.source}
                  </span>
                </td>
                <td className="px-3 py-3 text-right font-semibold tabular-nums text-foreground">{money(r.revenue)}</td>
                <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">{r.carts.toLocaleString()}</td>
                <td className="px-5 py-3 text-right sm:px-6">
                  <span className="inline-flex items-center gap-1 font-medium tabular-nums text-emerald-600 dark:text-emerald-400">
                    <TrendingUp className="h-3.5 w-3.5" /> {r.roas.toFixed(1)}x
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="border-t px-5 py-3 text-xs text-muted-foreground sm:px-6">
        Sample data. Your dashboard runs on your live orders, reconciled from GA4, Meta CAPI and your store&rsquo;s order webhooks.
      </p>
    </div>
  )
}
